if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      this.requestNumber = 0;
      this.ownedModalIds = new Set();
      this.preprocess = this.preprocessHTML.bind(this);
      this.addEventListener('product-info:loaded', ({ target }) => {
        if (this.modalContent.contains(target)) target.addPreProcessCallback(this.preprocess);
      });
    }

    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      const section = this.closest('.shopify-section');
      if (section) this.dataset.section = section.id.replace('shopify-section-', '');
      if (this.parentElement !== document.body) document.body.appendChild(this);
    }

    disconnectedCallback() { this.cancelRequest(); this.clearProduct(); }

    cancelRequest() {
      ++this.requestNumber;
      this.abortController?.abort();
      clearTimeout(this.requestTimeout);
      this.setOpenerLoading(this.loadingOpener, false);
      this.loadingOpener = null;
    }

    setOpenerLoading(opener, loading) {
      if (!opener) return;
      if (loading) opener.setAttribute('aria-disabled', 'true'); else opener.removeAttribute('aria-disabled');
      opener.classList.toggle('loading', loading);
      opener.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
    }

    productRequestUrl(value) {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin || !/^https?:$/.test(url.protocol)) throw new Error('Invalid product URL');
      const current = new URL(window.location.href);
      const theme = window.Shopify?.theme;
      const explicitPreview = current.searchParams.get('preview_theme_id');
      const previewId = /^\d+$/.test(explicitPreview || '') ? explicitPreview
        : (['unpublished', 'development'].includes(theme?.role) || window.Shopify?.designMode) && /^\d+$/.test(String(theme?.id || '')) ? String(theme.id) : null;
      // Preview cookies are not reliable in every editor/preview context. Keep
      // the selected theme explicit; ordinary live requests get no preview ID.
      if (previewId) {
        url.searchParams.set('preview_theme_id', previewId);
        ['preview_key', 'key', '_fd', '_sc', '_ab'].forEach(name => {
          if (current.searchParams.has(name)) url.searchParams.set(name, current.searchParams.get(name));
        });
      }
      return url.pathname + url.search;
    }

    clearProduct() {
      this.ownedModalIds.forEach(id => {
        const modal = document.getElementById(id);
        if (modal?.hasAttribute('open')) modal.hide?.(true);
        modal?.remove();
      });
      this.ownedModalIds.clear();
      this.modalContent.replaceChildren();
      this.modalContent.removeAttribute('aria-busy');
      this.productView = null;
    }

    loadError(code) {
      const error = new Error('Quick view could not be loaded');
      error.cnQuickViewCode = code;
      return error;
    }

    selectProduct(html, requestUrl) {
      const request = new URL(requestUrl, window.location.origin);
      const requestedHandle = request.pathname.match(/\/products\/([^/]+)\/?$/)?.[1];
      const requestedVariant = request.searchParams.get('variant');
      const candidates = [...html.querySelectorAll('product-info[id^="MainProduct-"]')];
      let variantMismatch = false;
      for (const product of candidates) {
        if (!(product.dataset.originalSection || product.dataset.section)) continue;
        let productHandle = product.dataset.cnHandle;
        if (!productHandle && product.dataset.url) {
          try { productHandle = new URL(product.dataset.url, window.location.origin).pathname.match(/\/products\/([^/]+)\/?$/)?.[1]; } catch { continue; }
        }
        if (!requestedHandle || !productHandle || decodeURIComponent(productHandle) !== decodeURIComponent(requestedHandle)) continue;
        const variant = product.querySelector('product-form form[action*="/cart/add"] input[name="id"]') || product.querySelector('form[action*="/cart/add"] input[name="id"]');
        if (!variant?.value || (requestedVariant && variant.value !== requestedVariant)) { variantMismatch = true; continue; }
        return product;
      }
      throw this.loadError(variantMismatch ? 'variant-mismatch' : 'main-product-missing');
    }

    async fetchProduct(url, signal) {
      const fetchCandidate = async value => {
        let response;
        try { response = await fetch(value, {signal, credentials:'same-origin'}); } catch (error) { if (signal.aborted) throw error; throw this.loadError('product-network'); }
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        if (response.ok === false) throw this.loadError('product-http');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        return this.selectProduct(html, value);
      };
      try {
        return {product:await fetchCandidate(url), view:new URL(url, window.location.origin).searchParams.get('view')};
      } catch (error) {
        if (signal.aborted || error.name === 'AbortError') throw error;
        const fallback = new URL(url, window.location.origin);
        if (fallback.searchParams.get('view') === 'cn-quick-view') throw error;
        fallback.searchParams.set('view', 'cn-quick-view');
        fallback.searchParams.delete('section_id');
        // Keep product, variant, locale and preview context; only the dedicated
        // template changes. A different variant is never a valid fallback.
        return {product:await fetchCandidate(fallback.pathname + fallback.search), view:'cn-quick-view'};
      }
    }

    hide(preventFocus = false) {
      this.cancelRequest();
      this.clearProduct();
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      cart?.setActiveElement(this.openedBy);
      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    async show(opener) {
      if (!opener || (this.loadingOpener === opener && this.hasAttribute('open'))) return;
      this.cancelRequest();
      this.clearProduct();
      const requestNumber = this.requestNumber;
      this.abortController = new AbortController();
      const controller = this.abortController;
      let timedOut = false;
      const timeout = new Promise((resolve, reject) => {
        this.requestTimeout = setTimeout(() => {
          timedOut = true; controller.abort(); reject(new Error('Product request timed out'));
        }, 15000);
      });
      const aborted = new Promise((resolve, reject) => controller.signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')), {once:true}));
      // Closing the modal must also release component-definition waits.
      const deadline = Promise.race([timeout, aborted]);
      deadline.catch(() => {});
      let stage = 'open-modal';
      delete this.dataset.cnLoadError;
      this.loadingOpener = opener;
      this.setOpenerLoading(opener, true);
      const loading = document.createElement('p');
      loading.className = 'cn-quick-view-state';
      loading.setAttribute('role', 'status');
      loading.textContent = 'Produkt wird geladen …';
      this.modalContent.append(loading);
      this.modalContent.setAttribute('aria-busy', 'true');
      try {
        super.show(opener);
        const url = this.productRequestUrl(opener.getAttribute('data-product-url'));
        stage = 'product-fetch';
        const {product, view} = await Promise.race([this.fetchProduct(url, controller.signal), deadline]);
        if (requestNumber !== this.requestNumber || !this.hasAttribute('open') || !this.isConnected) return;
        this.sourceSection = product.dataset.originalSection || product.dataset.section;
        this.productView = view;
        stage = 'product-render';
        const requiredComponents = ['product-info', 'product-form', 'media-gallery', 'product-modal'].filter(tag => product.matches(tag) || product.querySelector(tag));
        this.preprocessHTML(product);
        HTMLUpdateUtility.setInnerHTML(this.modalContent, product.outerHTML);
        stage = 'component-loading';
        await Promise.race([Promise.all(requiredComponents.map(tag => customElements.whenDefined(tag))), deadline]);
        if (requestNumber !== this.requestNumber || !this.hasAttribute('open') || !this.isConnected) return;
        const info = this.modalContent.querySelector('product-info'), form = info?.querySelector('product-form');
        if (!info?.handleUpdateProductInfo || (form && !form.form)) throw this.loadError('component-initialization');
        this.modalContent.removeAttribute('aria-busy');
        // Optional integrations must not replace a successfully loaded product
        // with an error if an app or payment/XR initializer is unavailable.
        try { window.Shopify?.PaymentButton?.init?.(); } catch { console.warn('Quick view: payment initialization deferred.'); }
        try { window.ProductModel?.loadShopifyXR?.(); } catch { console.warn('Quick view: 3D initialization deferred.'); }
        // Refresh endpoints without resetting a shopper who already focused
        // the persistent close control while the product request was pending.
        if (typeof trapFocus === 'function') trapFocus(this, this.contains(document.activeElement) ? document.activeElement : this.querySelector('[role="dialog"]'));
      } catch (error) {
        if ((error.name === 'AbortError' && !timedOut) || requestNumber !== this.requestNumber || !this.hasAttribute('open')) return;
        const code = timedOut ? stage + '-timeout' : error.cnQuickViewCode || stage;
        this.dataset.cnLoadError = code;
        // Codes are deliberately bounded: never log URLs, preview keys or raw
        // response bodies when a shop app, network or component fails.
        console.warn('[CYBERNERDS quick-view]', code);
        this.clearProduct();
        this.renderError(opener);
      } finally {
        if (requestNumber === this.requestNumber) {
          clearTimeout(this.requestTimeout);
          this.modalContent.removeAttribute('aria-busy');
          this.setOpenerLoading(opener, false);
          this.loadingOpener = null;
        }
      }
    }

    renderError(opener) {
      const box = document.createElement('div'); box.className = 'cn-quick-view-state';
      const message = document.createElement('p'); message.setAttribute('role', 'alert');
      message.textContent = 'Die Schnellansicht konnte nicht geladen werden.';
      const retry = document.createElement('button'); retry.type = 'button'; retry.textContent = 'Erneut versuchen';
      retry.addEventListener('click', () => this.show(opener));
      const link = document.createElement('a'); link.textContent = 'Produktseite öffnen';
      try {
        link.href = this.productRequestUrl(opener.getAttribute('data-product-url'));
      } catch { /* A malformed URL still leaves the dialog closable and retryable. */ }
      box.append(message, retry); if (link.hasAttribute('href')) box.append(link);
      this.modalContent.replaceChildren(box);
      const focused = document.activeElement;
      const target = this.contains(focused) && focused !== this.querySelector('[role="dialog"]') ? focused : retry;
      try { if (typeof trapFocus === 'function') trapFocus(this, target); } catch { target.focus(); }
    }

    preprocessHTML(node) {
      if (!node) return;
      const original = node.dataset?.originalSection || node.dataset?.section || this.sourceSection;
      if (!original) return;
      const oldSection = original.startsWith('quickadd-') ? (this.sourceSection || original.slice(9)) : original;
      const newSection = `quickadd-${oldSection}`;
      const remap = value => value.includes(newSection) ? value : value.replaceAll(oldSection, newSection);
      const elements = [node, ...node.querySelectorAll('*')];
      const ids = new Map();
      elements.forEach(element => {
        // A native cart form has an input named "id". Real browsers may expose
        // that control as form.id, shadowing Element's string ID property.
        // Read/write the attribute so variant inputs cannot clobber DOM IDs.
        const originalId = element.getAttribute('id');
        if (!originalId) return;
        const next = originalId.includes(oldSection) ? remap(originalId) : originalId.startsWith(`${newSection}-`) ? originalId : `${newSection}-${originalId}`;
        ids.set(originalId, next); element.setAttribute('id', next);
      });
      elements.forEach(element => {
        ['for', 'form', 'aria-controls', 'aria-labelledby', 'aria-describedby', 'aria-owns', 'list', 'headers'].forEach(name => {
          if (element.hasAttribute(name)) element.setAttribute(name, element.getAttribute(name).split(/\s+/).map(id => ids.get(id) || remap(id)).join(' '));
        });
        ['href', 'data-modal'].forEach(name => {
          const value = element.getAttribute(name);
          if (value?.startsWith('#')) element.setAttribute(name, '#' + (ids.get(value.slice(1)) || remap(value.slice(1))));
        });
        ['data-section', 'data-section-id', 'data-target', 'data-media-id', 'data-product-form', 'name'].forEach(name => {
          const value = element.getAttribute(name);
          if (value?.includes(oldSection)) element.setAttribute(name, remap(value));
        });
        if (element.classList) [...element.classList].forEach(name => {
          if (name.includes(oldSection)) element.classList.replace(name, remap(name));
        });
        if (element.tagName === 'STYLE') element.textContent = element.textContent.replaceAll(`section-${oldSection}-padding`, `section-${newSection}-padding`);
      });
      if (node.matches('product-info')) {
        node.dataset.originalSection = oldSection;
        node.dataset.section = newSection;
        node.dataset.updateUrl = 'false';
        node.removeAttribute('data-zoom-on-hover');
        node.querySelectorAll('pickup-availability,modal-dialog').forEach(element => element.remove());
        node.querySelectorAll('product-modal').forEach(modal => {
          modal.dataset.quickViewOwner = this.id;
          this.ownedModalIds.add(modal.id);
        });
      }
      node.querySelectorAll('.product__media img').forEach(img => img.sizes = '(min-width: 850px) 480px, calc(100vw - 64px)');
    }
  });
}
